import React, { ReactElement } from 'react';

import { Divider, Grid } from '@mui/material';

import { LogViewTableRow, LogViewTableRowProps } from '../../LogViewerTableRow';
import { shouldPassOnClick } from '../shouldPassOnClick';
import { LogEntryGenericCell, LogEntryGenericCellModel } from './cells/LogEntryGenericCell';
import { LogEntryMQTTCell, LogEntryMQTTCellModel } from './cells/LogEntryMQTTCell';
import { LogEntryNetworkCellModel } from './cells/LogEntryNetworkCellModel';
import {
  LogEntryReduxActionCell,
  LogEntryReduxActionCellModel,
} from './cells/LogEntryReduxActionCell';
import { NetworkBodyWithJqCell, NetworkBodyWithJqCellModel } from './cells/NetworkBodyWithJqCell';
import { NetworkRequestCell } from './cells/NetworkRequestCell';
import { NetworkResponseCell } from './cells/NetworkResponseCell';
import { Header } from './components/Header';
import { RawContent } from './components/RawContent';
import { LogEntryCellModel, LogEntryCellType } from './LogEntryCellModel';
import { LogEntryRowModel } from './LogEntryRowModel';

interface Props extends LogViewTableRowProps {
  model: LogEntryRowModel;
  onHoverChange?: (hovered: boolean) => void;
}

export class LogEntryRow extends LogViewTableRow<Props> {
  render() {
    const cellElements = this.cells();
    const gridItems = this.gridItems(cellElements);

    const modelData = this.props.model.data;

    return (
      <div
        className={`logViewerTableRow logViewerTableLogEntryRow ${
          modelData.isSelected ? 'logViewerTableRowSelected' : ''
        }`}
        onClick={(e) => {
          if (shouldPassOnClick(e.nativeEvent.composedPath())) {
            this.props.onClick();
          }
        }}
        onMouseEnter={() => {
          this.props.onHoverChange?.(true);
        }}
        onMouseLeave={() => {
          this.props.onHoverChange?.(false);
        }}
      >
        <Header
          modelData={modelData.header}
          isSelected={modelData.isSelected}
          isBookmarked={modelData.isBookmarked}
          passesFilter={modelData.passesFilter}
          onSelectRowAsBaseForTimeOffset={() => {
            this.props.model.delegate.onSelectRowAsBaseForTimeOffset(this.props.model);
          }}
          onAddBookmark={() => {
            this.props.model.delegate.onRequestAddToBookmarks(this.props.model);
          }}
          onDeleteBookmark={() => {
            this.props.model.delegate.onRequestDeleteFromBookmarks(this.props.model);
          }}
        />

        <Grid
          container
          wrap="nowrap"
          spacing={1}
          className="gridContainer"
          columns={cellElements.length}
        >
          {gridItems}
        </Grid>

        <RawContent
          modelData={modelData.rawContent}
          onDataChange={(change) => {
            this.props.model.delegate.onRowDataChange({
              rowModel: this.props.model,
              change: {
                rawContent: change,
              },
              postProcess: true,
            });
          }}
        />

        <div className="lineBetweenRows" />
      </div>
    );
  }

  private gridItems(cellElements: ReactElement[]): ReactElement[] {
    if (cellElements.length <= 0) {
      return [];
    }

    const cellElementsToDisplay = cellElements;

    const gridItems: ReactElement[] = [];
    cellElementsToDisplay.forEach((cell, index) => {
      if (index > 0) {
        gridItems.push(<Divider orientation="vertical" key={`divider_${index}`} flexItem />);
      }

      const gridItem = (
        <Grid item className="gridItem" key={`item_${index}`} xs={1}>
          {cell}
        </Grid>
      );
      gridItems.push(gridItem);
    });

    return gridItems;
  }

  private cells(): ReactElement[] {
    const mainCellModel = this.props.model.mainCell;
    const cellModels = [mainCellModel].concat(this.props.model.additionalCells);

    return cellModels.map((model) => this.cell(model));
  }

  private cell(model: LogEntryCellModel): ReactElement {
    let result: ReactElement;

    switch (model.data.type) {
      case LogEntryCellType.Generic:
        result = <LogEntryGenericCell model={model as LogEntryGenericCellModel} />;
        break;
      case LogEntryCellType.ReduxAction:
        result = <LogEntryReduxActionCell model={model as LogEntryReduxActionCellModel} />;
        break;
      case LogEntryCellType.NetworkRequest:
        result = <NetworkRequestCell model={model as LogEntryNetworkCellModel} />;
        break;
      case LogEntryCellType.NetworkResponse:
        result = <NetworkResponseCell model={model as LogEntryNetworkCellModel} />;
        break;
      case LogEntryCellType.NetworkBodyWithJq:
        result = <NetworkBodyWithJqCell model={model as NetworkBodyWithJqCellModel} />;
        break;
      case LogEntryCellType.MQTTMessage:
        result = <LogEntryMQTTCell model={model as LogEntryMQTTCellModel} />;
        break;
    }

    return result;
  }
}
